"use client"

import { Stack, VStack } from "@chakra-ui/react"
import { type FC } from "react"
import { useId } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import Compare from "@/components/Compare"
import MarkdownParser from "@/components/MarkdownParser"
import { type CompareBlock as CompareBlockType } from "@/types/database"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface CompareBlockProps extends ContentBlockTitleNumberProps {
  block: CompareBlockType
}

const CompareBlock: FC<CompareBlockProps> = ({ block, titleNumber }) => {
  const {
    darker_bg,
    description,
    first_image,
    first_image_label = "Before",
    padding,
    second_image,
    second_image_label = "After",
    title,
  } = block

  const trimmedTitle = title?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription =
    description && description !== "" && description !== null

  const id = useId()
  return (
    <Stack
      alignItems="flex-start"
      aria-labelledby={`compare-heading-${id}`}
      bgColor={
        padding ? (darker_bg ? "bg.subtle" : "transparent") : "bg.subtle"
      }
      className="compare-block edge-to-edge"
      direction={{ base: "column" }}
      gap={{ base: 8 }}
      position="relative"
      px={{
        base: padding ? 8 : 0,
        md: padding ? 10 : 0,
        lg: padding ? 24 : 0,
        xl: padding ? 40 : 0,
      }}
    >
      {hasTitle && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          mt={padding ? 0 : { base: 12, lg: 24 }}
          px={{
            base: padding ? 0 : 8,
            md: padding ? 0 : 10,
            lg: padding ? 0 : 24,
            xl: padding ? 0 : 40,
          }}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="center"
        borderRadius={padding ? "3xl" : "none"}
        position="relative"
        px={{
          base: padding ? 8 : 0,
          md: padding ? 10 : 0,
          lg: padding ? 24 : 0,
          xl: padding ? 40 : 0,
        }}
        width="full"
      >
        <BlurFade inView>
          <Compare
            firstImage={`${process.env.DIRECTUS_ASSETS_URL}${first_image?.id}?quality=100&width=1376`}
            firstImageLabel={first_image_label}
            initialSliderPercentage={50}
            roundedCorners={padding ? "3xl" : "none"}
            secondImage={`${process.env.DIRECTUS_ASSETS_URL}${second_image?.id}?quality=100&width=1376`}
            secondImageLabel={second_image_label}
            showHandlebar={true}
            slideMode="hover"
          />
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default CompareBlock
